import {useState, useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {authClient} from '../auth-client'
import {
  AuthLayout, AuthLink, ErrorBanner, fieldClass, primaryButtonClass, secondaryButtonClass
} from './auth-layout'

export function SignInPage() {
  const {t} = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | undefined>()
  const [unverified, setUnverified] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = useCallback(async (event: React.SyntheticEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(undefined)
    setUnverified(false)

    try {
      const {error} = await authClient.signIn.email({email, password, callbackURL: '/'})

      if (error) {
        // Better Auth flags unverified accounts with a 403 + EMAIL_NOT_VERIFIED code.
        if (error.status === 403 || error.code === 'EMAIL_NOT_VERIFIED') {
          setUnverified(true)
        } else {
          setError(t('auth.signIn.errorDefault'))
        }

        setSubmitting(false)
        return
      }

      // Full reload so the core AuthProvider re-fetches the session and the app
      // shell sees the authenticated user (a client-side navigate would keep the
      // stale "anonymous" auth state and bounce back to /sign-in).
      globalThis.location.href = '/'
    } catch {
      // Network/CORS failure: surface a generic error instead of hanging.
      setError(t('auth.signIn.errorDefault'))
      setSubmitting(false)
    }
  }, [email, password, t])

  const handlePasskey = useCallback(async () => {
    setError(undefined)
    const {error} = await authClient.signIn.passkey()
    if (error) {
      setError(t('auth.signIn.passkeyError'))
      return
    }

    globalThis.location.href = '/'
  }, [t])

  const handleResend = useCallback(async () => {
    await authClient.sendVerificationEmail({email, callbackURL: `${globalThis.location.origin}/`})
    setUnverified(false)
    setError(undefined)
  }, [email])

  return (
    <AuthLayout
      title={t('auth.signIn.title')}
      subtitle={t('auth.signIn.subtitle')}
      footer={<>{t('auth.signIn.noAccount')} <AuthLink to='/sign-up'>{t('auth.signIn.createAccount')}</AuthLink></>}
    >
      <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
        {error && <ErrorBanner message={error} />}
        {unverified && (
          <div className='rounded-md border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400'>
            {t('auth.signIn.unverified')}{' '}
            <button type='button' className='font-medium underline' onClick={handleResend}>
              {t('auth.signIn.resendVerification')}
            </button>
          </div>
        )}

        <input
          type='email' placeholder={t('auth.fields.email')} value={email}
          required autoFocus
          autoComplete='email webauthn' className={fieldClass}
          onChange={e => {
            setEmail(e.target.value)
          }}
        />
        <div className='flex flex-col gap-1'>
          <input
            type='password' placeholder={t('auth.fields.password')} value={password}
            required
            autoComplete='current-password' className={fieldClass}
            onChange={e => {
              setPassword(e.target.value)
            }}
          />
          <div className='text-right text-sm'>
            <AuthLink to='/forgot-password'>{t('auth.signIn.forgotPassword')}</AuthLink>
          </div>
        </div>

        <button type='submit' disabled={submitting} className={primaryButtonClass}>
          {submitting ? t('auth.signIn.submitting') : t('auth.signIn.submit')}
        </button>
      </form>

      <button type='button' className={secondaryButtonClass} onClick={handlePasskey}>
        {t('auth.signIn.passkey')}
      </button>
    </AuthLayout>
  )
}
